/* global Routes */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import styles from './LeftSidebar.scss'
import { Surfaces } from './Surfaces'
import { icons } from '../icons'
import { useForceUpdate } from '../useForceUpdate'
import { Distance, Elevation, Duration } from 'common/units'
import { MetricUnitsContext } from 'common/units/MetricUnitsContext'
import { CTA } from 'common/styleguide/CTA'
import { ConfirmationDialog } from 'common/styleguide/ConfirmationDialog'
import { PopoverMenu, PopoverMenuItem } from 'common/styleguide/PopoverMenu'
import { Msg, featureNames, commonNouns, commonVerbs, commonCTAs, useFormatMessage } from 'lib/localization'
import { experimentEnabled } from 'lib/utils'
import { FormattedMessage as T, defineMessages, injectIntl } from 'react-intl'
import _ from 'lodash'
import cx from 'classnames'
import uuid from 'uuid/v4'

export const messages = defineMessages({
  unnamedRoute: {
    id: 'LeftSidebar.unnamedRoute',
    defaultMessage: 'Unnamed route'
  },
  setName: {
    id: 'LeftSidebar.setName',
    defaultMessage: 'Set Name'
  },
  estimatedTimeTip: {
    id: 'LeftSidebar.estimatedTimeTip',
    defaultMessage: 'Estimated time to ride this route, based on your riding history and adjusted for difficulty.'
  },
  reviewCuesTip: {
    id: 'LeftSidebar.reviewCuesTip',
    defaultMessage: 'Take a guided tour through your cuesheet to familiarize yourself with the route and verify accuracy before saving.'
  },
  removeRoute: {
    id: 'LeftSidebar.removeRoute',
    defaultMessage: 'Remove Route'
  }
})

export const LeftSidebar = ({
  activate,
  setVisible,
  removeRoute,
  revertToLast,
  saveRoute,
  launchReviewCues,
  highlight,
  unhighlight,
  startNewRoute,
  loadExistingModal,
  plannerState,
  showSurfaceDebug
}) => {
  const needsUpgrade = !window.rwgps.config.privileges.advanced_planning
  const fm = useFormatMessage()

  // Handle unit changes and route updates that happen outside this React hierarchy
  const [metricUnits, setMetricUnits] = useState(window.rwgps.LabelMaker.metric)
  const handleUnitsChange = () => {
    setMetricUnits(window.rwgps.LabelMaker.metric)
  }
  const forceUpdate = useForceUpdate()
  useEffect(() => {
    window.rwgps.Events.bind('route:modified', forceUpdate)
    window.rwgps.Events.bind('map:routes_changed', forceUpdate)
    window.rwgps.Events.bind('units_change', handleUnitsChange)
    window.rwgps.Events.bind('cp:populate', forceUpdate)
    return () => {
      window.rwgps.Events.unbind('route:modified', forceUpdate)
      window.rwgps.Events.unbind('map:routes_changed', forceUpdate)
      window.rwgps.Events.unbind('units_change', handleUnitsChange)
      window.rwgps.Events.unbind('cp:populate', forceUpdate)
    }
  }, [])

  // Send cuesheet interactions to the map
  const [hoveredCue, setHoveredCue] = useState()
  useEffect(() => {
    if (hoveredCue) {
      Routes.activeMap.openCoursePointInfoWindow(hoveredCue, false)
    } else {
      Routes.activeMap.closePreviewInfoWindow()
    }
  }, [hoveredCue])
  const selectCue = cue => {
    Routes.activeMap.openCoursePointInfoWindow(cue, true)
  }

  // Show/hide route on the map
  const toggleVisibility = route => {
    setVisible(route, !route.drawInactive)
    forceUpdate()
  }

  // Share vertical space between a dynamic-height route list and a scrolling cuesheet
  const cuesheetRef = useRef()
  const saveRef = useRef()
  const onResize = _.throttle(() => {
    cuesheetRef.current.style.height = (
      window.innerHeight - (cuesheetRef.current.getClientRects()[0] || {}).top - saveRef.current.offsetHeight
    ) + 'px'
  }, 50, { leading: true, trailing: true })
  useEffect(() => {
    window.addEventListener('resize', onResize)
    // having resize issues that we should resolve when we introduce an RP parent, but this is harmless and fixes it up while the layout settles
    const interval = setInterval(() => window.dispatchEvent(new window.Event('resize')), 200)
    setTimeout(() => {
      clearInterval(interval)
    }, 5000)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useLayoutEffect(onResize, [Routes.activeMap?.routes.length])

  const activeRoute = Routes.activeMap?.routes.find(r => r.active)
  const [confirmRemoveRoute, setConfirmRemoveRoute] = useState()
  const [confirmRevertToLast, setConfirmRevertToLast] = useState()
  const [editRoute, setEditRoute] = useState()
  const hasCues = activeRoute?.coursePoints().length > 0

  const saveDisabled = !(activeRoute?.metrics.distance || activeRoute?.pois.length)

  // Oct 27, 2020
  // this is a HACK, and should be removed in favor of adding a uuid in the CoursePoint constructor,
  // but this fixes a cue duplication bug in prod right now
  activeRoute && _.flatten(activeRoute.segments.map(s => s.coursePoints)).forEach(cp => { if (!cp.uuid) cp['uuid'] = uuid() })

  const mcRef = useRef(null)
  const calculatedRef = useRef(null)
  const [surfaceI, setSurfaceI] = useState(0)
  const trackPoints = activeRoute?.trackPoints()

  // this is a bit of a hack, but it allows us to force the mc that lives in
  // this component to update in response to trackPoint surface value changes
  // (ie when the `change surface` tool is used)
  useEffect(() => {
    const recalculate = () => setSurfaceI(surfaceI + 1)
    window.rwgps.Events.bind('surfacesChanged', recalculate)
    return () => window.rwgps.Events.unbind('surfacesChanged', recalculate)
  }, [surfaceI])

  useMemo(() => {
    if (trackPoints?.length > 0) {
      // optimially we'd be able to inspect the sampleGraph's mc here, but this
      // component mounts before that runs, and listening to non react state
      // changes to rerun useMemo seems to be a no go
      mcRef.current = new window.rwgps.MetricsCalculator(trackPoints, false)
      mcRef.current.calculate()
      calculatedRef.current = mcRef.current.calculateSurfaceTypes()
    } else {
      calculatedRef.current = {}
    }
  }, [activeRoute?.__uniqueKey, trackPoints, surfaceI])

  return <MetricUnitsContext.Provider value={metricUnits}>
    <div className={styles.LeftSidebar}>
      <div className={styles.boxes}>
        <div onClick={() => loadExistingModal()} className={cx(needsUpgrade && styles.needsUpgrade)}>
          {icons.plus}
          <T id='LeftSidebar.importExisting' defaultMessage='Import Existing' />
        </div>
        <div onClick={() => startNewRoute()} className={cx(needsUpgrade && styles.needsUpgrade)}>
          {icons.plus}
          <T id='LeftSidebar.addNew' defaultMessage='Add New' />
        </div>
      </div>
      <div style={{ margin: '10px' }} className={styles.title}>{fm(featureNames.routes)}</div>
      <div className={styles.routes}>
        {Routes.activeMap?.routes.map(route => {
          if (!route.__uniqueKey) route.__uniqueKey = Math.random().toString(36).substring(2)
          return (
            <RouteRow
              key={route.__uniqueKey}
              allowDelete={route.metrics.distance > 0 || Routes.activeMap.routes.length > 1}
              activate={activate}
              highlight={highlight}
              unhighlight={unhighlight}
              {...{ setEditRoute, setConfirmRemoveRoute, setConfirmRevertToLast, route, toggleVisibility }} />
          )
        })}
      </div>
      <div ref={cuesheetRef} className={styles.cuesheet}>
        {experimentEnabled('surface_types') && <Surfaces
          key={trackPoints?.length}
          surfaceTypes={calculatedRef.current}
          hoveredFeature={plannerState.hoveredFeature}
          showSurfaceDebug={showSurfaceDebug}
        />}
        <div className={styles.cuesheetHeader}>
          <span className={styles.cuesheetTitle}>{fm(commonNouns.cuesheet)}</span>
        </div>
        {activeRoute?.coursePoints().map(cp => (
          <div key={cp.uuid} className={styles.cueRow} onClick={() => selectCue(cp)} onMouseEnter={() => setHoveredCue(cp)} onMouseLeave={() => setHoveredCue(null)}>
            <div className={styles.cueRowInner}>
              <div className={styles.cue}>{cp.notes}</div>
              <div className={styles.distance}><Distance minimumFractionDigits={1} short bigUnits value={cp.distance} /></div>
            </div>
          </div>
        ))}
        {!hasCues && (
          <div className={styles.emptyCuesheet}>
            {activeRoute?.metrics.distance && <T id='LeftSidebar.routeWithoutCues' defaultMessage='This route has no cues. Create a cuesheet with the Trace tool or by adding Custom Cues.' />}
            {!activeRoute?.metrics.distance && <T id='LeftSidebar.emptyRouteNoCues' defaultMessage='Click on map to generate cuesheet' />}
          </div>
        )}
      </div>
      <div ref={saveRef} className={styles.save}>
        <a
          data-tip
          title={fm(messages.reviewCuesTip)}
          className={cx(styles.reviewCuesCTA, (!hasCues || needsUpgrade) && styles.disabled)}
          onClick={() => hasCues && launchReviewCues(activeRoute)}>
          <T id='LeftSidebar.reviewCues' defaultMessage='Review Cues' />
        </a>
        <CTA
          disabled={saveDisabled}
          className={styles.saveCTA}
          onClick={() => !saveDisabled && saveRoute()}>
          {fm(commonVerbs.save)}
        </CTA>
      </div>
      {confirmRemoveRoute && <ConfirmationDialog
        onRequestClose={() => setConfirmRemoveRoute(null)}
        onCancel={() => setConfirmRemoveRoute(null)}
        onConfirm={() => { removeRoute(confirmRemoveRoute); setConfirmRemoveRoute(null) }}>
        <T id='LeftSidebar.confirmRemove' defaultMessage='Are you sure you want to remove this route from the map?' />
      </ConfirmationDialog>}
      {confirmRevertToLast && <ConfirmationDialog
        onRequestClose={() => setConfirmRevertToLast(null)}
        onCancel={() => setConfirmRevertToLast(null)}
        onConfirm={() => { revertToLast(confirmRevertToLast); setConfirmRevertToLast(null) }}>
        <T id='LeftSidebar.confirmRevert' defaultMessage='Are you sure you want to revert to the last time this route was saved?' />
      </ConfirmationDialog>}
      {editRoute && <EditDialog
        name={editRoute.name}
        onRequestClose={() => setEditRoute(null)}
        onConfirm={name => { editRoute.name = name; setEditRoute(null) }} />}
    </div>
  </MetricUnitsContext.Provider>
}

const _RouteRow = ({ route, allowDelete, activate, setConfirmRemoveRoute, setConfirmRevertToLast, setEditRoute, toggleVisibility, highlight, unhighlight, intl }) => {
  const fm = useFormatMessage()
  const [menuExpanded, setMenuExpanded] = useState(false)
  const tripleDotRef = useRef()
  return (
    <div
      className={cx(styles.route, route.active && styles.active)}
      onClick={() => !route.active && !route.connectionPointDisabled && activate(route)}
      onMouseEnter={() => !route.active && highlight(route)}
      onMouseLeave={() => !route.active && unhighlight(route)}
    >
      <div style={{ display: 'flex' }}>
        {route.active && icons.checkmark}
        <div className={styles.nameAndMetrics}>
          <div className={styles.title}>{route.name || fm(messages.unnamedRoute)}</div>
          <div className={styles.metricsContainer}>
            <div className={styles.metrics}>
              <span><Distance short bigUnits value={route.metrics.distance ?? 0} /></span>
              <span><Elevation short value={route.metrics.ele_gain ?? 0} /></span>
              {route.metrics.estimatedTime && <span data-tip data-tippy-placement='bottom-end' title={fm(messages.estimatedTimeTip)} style={{ fontStyle: 'italic' }}>
                <Duration value={route.metrics.estimatedTime} biggestUnits />
              </span>}
            </div>
          </div>
        </div>
      </div>
      <span ref={tripleDotRef} className={styles.tripleDot} onClick={e => { e.stopPropagation(); setMenuExpanded(!menuExpanded) }}>
        {icons.tripleDot}
      </span>
      {menuExpanded && <PopoverMenu intl={intl} className={styles.routeMenu} target={tripleDotRef.current} placement='bottom' onRequestClose={() => setMenuExpanded(false)}>
        {!route.active && <PopoverMenuItem onClick={e => { e.stopPropagation(); setMenuExpanded(false); toggleVisibility(route) }}>
          {route.drawInactive && <T id='LeftSidebar.hideOnMap' defaultMessage='Hide on Map' />}
          {!route.drawInactive && <T id='LeftSidebar.showOnMap' defaultMessage='Show on Map' />}
        </PopoverMenuItem>}
        <PopoverMenuItem onClick={e => { e.stopPropagation(); setMenuExpanded(false); setEditRoute(route) }}>
          {fm(messages.setName)}
        </PopoverMenuItem>
        {route.routeData?.id && <PopoverMenuItem onClick={e => { e.stopPropagation(); setMenuExpanded(false); setConfirmRevertToLast(route) }}>
          <T id='LeftSidebar.revertToLast' defaultMessage='Revert to Last Save' />
        </PopoverMenuItem>}
        {route.routeData?.id && <PopoverMenuItem>
          <a href={`/${route.routeData.isTrip ? 'trips' : 'routes'}/${route.routeData.id}`} target='_blank'>
            <T id='LeftSidebar.open' defaultMessage='Open in New Tab' />
          </a>
        </PopoverMenuItem>}
        {allowDelete && <PopoverMenuItem onClick={e => { e.stopPropagation(); setMenuExpanded(false); setConfirmRemoveRoute(route) }}>
          {fm(messages.removeRoute)}
        </PopoverMenuItem>}
      </PopoverMenu>}
    </div>
  )
}

const RouteRow = injectIntl(_RouteRow)

const EditDialog = ({ name, onRequestClose, onConfirm }) => {
  const fm = useFormatMessage()
  const [_name, setName] = useState(name)

  const inputRef = useRef()
  useEffect(() => {
    // the confirmation dialog doesn't appear immediately so we have to wait for it.
    const interval = setInterval(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
        clearInterval(interval)
      }
    }, 20)
  }, [])

  return (
    <ConfirmationDialog
      title={fm(messages.setName)}
      maxWidth='600px'
      onRequestClose={onRequestClose}
      onCancel={onRequestClose}
      onConfirm={() => onConfirm(_name)}
      confirmText={fm(commonCTAs.ok)}>
      <div>
        <label><Msg capitalized m={commonNouns.name} /></label>
        <input
          ref={inputRef}
          type='text'
          value={_name}
          onKeyDown={e => e.keyCode === 13 && onConfirm(_name)}
          onChange={e => setName(e.target.value)} />
      </div>
    </ConfirmationDialog>
  )
}
